/* @flow */

import React from 'react';
import RequireForAccess from 'react-access';
import { computed } from 'mobx';

import type { RoleList, RoleId } from 'permissions/defs';
import features from 'features';
import type { FeatureFlag } from 'features';
import { ENV } from 'config';

export type FeatureFlagProps = {
  name: string,
};

const resolveFeatureDescription = (name: string) => {
  if (!(name in features)) {
    console.error(`Feature Flag '${name}' is being used but not defined in the features list.`);

    return {
      enabled: false,
    };
  }

  const relevantRules = features[name]
    .filter(rule => !rule.envs || rule.envs.includes(ENV))
    .filter(rule => rule.enabled);

  if (relevantRules.length > 0) {
    const result: FeatureFlag = { enabled: true };

    for (const rule of relevantRules) {
      if (!rule.restrictToRoles) {
        delete result.restrictToRoles;
      } else {
        result.restrictToRoles = result.restrictToRoles || [];
        result.restrictToRoles = Array.from(
          new Set([...result.restrictToRoles, ...rule.restrictToRoles])
        );
      }
    }

    return result;
  }

  return { enabled: false };
};

export default class FeatureFlagComponent extends React.Component<FeatureFlagProps> {
  @computed get featureDescription(): FeatureFlag {
    const { name } = this.props;

    return resolveFeatureDescription(name);
  }

  render() {
    const { children } = this.props;

    if (!this.featureDescription.enabled) {
      return null;
    }

    if (this.featureDescription.restrictToRoles) {
      return (
        <RequireForAccess permissions={this.featureDescription.restrictToRoles}>
          {children}
        </RequireForAccess>
      );
    }

    return children;
  }
}

export const isEnabled = (featureName: string, roleId: RoleId): boolean => {
  const featureDescription = resolveFeatureDescription(featureName);

  if (featureDescription.restrictToRoles) {
    return featureDescription.restrictToRoles.includes(roleId);
  }

  return featureDescription.enabled;
};
